import React, { useState, useEffect } from 'react';
import { Upload, Globe, FileSpreadsheet, CheckCircle, AlertCircle, Clock, ArrowRight, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { useAuth } from '../../contexts/AuthContext';
import DocumentUpload from './DocumentUpload';
import WebsiteScraping from './WebsiteScraping';
import BulkImportUpload from './BulkImportUpload';

interface IngestionTool {
  id: string;
  title: string;
  description: string;
  icon: string;
  endpoint: string;
  enabled: boolean;
  features: string[];
}

interface WorkflowStep {
  step: number;
  title: string;
  description: string;
}

interface IngestionData {
  enterpriseId: string;
  title: string;
  description: string;
  tools: Record<string, IngestionTool>;
  workflow: {
    steps: WorkflowStep[];
  };
  stats: {
    totalExtractions: number;
    pendingReview: number;
    successRate: number;
  };
}

interface MenuIngestionToolsProps {
  enterpriseId: string;
  onClose?: () => void;
}

type ActiveTool = 'document-upload' | 'website-scraping' | 'bulk-import' | null;

const MenuIngestionTools: React.FC<MenuIngestionToolsProps> = ({ enterpriseId, onClose }) => {
  const { getIdToken } = useAuth();
  const [ingestionData, setIngestionData] = useState<IngestionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<ActiveTool>(null);

  useEffect(() => {
    fetchIngestionData();
  }, [enterpriseId]);

  const fetchIngestionData = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.menuIngestion(enterpriseId), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch ingestion data');
      }
      const data = await response.json();
      setIngestionData(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getToolIcon = (iconName: string) => {
    switch (iconName) {
      case 'document-upload':
        return <Upload className="w-8 h-8" />;
      case 'globe':
        return <Globe className="w-8 h-8" />;
      case 'spreadsheet':
        return <FileSpreadsheet className="w-8 h-8" />;
      default:
        return <Upload className="w-8 h-8" />;
    }
  };

  const handleToolSelect = (toolId: string) => {
    setSelectedTool(toolId);
  };

  const handleStartTool = (toolId: string) => {
    setActiveTool(toolId as ActiveTool);
  };

  const handleBackToTools = () => {
    setActiveTool(null);
    setSelectedTool(null);
  };

  const renderToolCard = (tool: IngestionTool) => (
    <div
      key={tool.id}
      className={`
        bg-white rounded-lg border-2 p-6 cursor-pointer transition-all duration-200
        ${selectedTool === tool.id 
          ? 'border-blue-500 bg-blue-50 shadow-lg' 
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
        }
        ${!tool.enabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      onClick={() => tool.enabled && handleToolSelect(tool.id)}
    >
      <div className="flex items-start space-x-4">
        <div className={`
          p-3 rounded-lg
          ${selectedTool === tool.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'}
        `}>
          {getToolIcon(tool.icon)}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {tool.title}
          </h3>
          <p className="text-gray-600 mb-3">
            {tool.description}
          </p>
          <div className="flex flex-wrap gap-2">
            {tool.features.map((feature, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>
      {selectedTool === tool.id && (
        <div className="mt-4 pt-4 border-t border-blue-200">
          <button
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleStartTool(tool.id);
            }}
          >
            Start {tool.title}
          </button>
        </div>
      )}
    </div>
  );

  const renderWorkflowStep = (step: WorkflowStep, index: number) => (
    <div key={step.step} className="flex items-start space-x-4">
      <div className="flex-shrink-0">
        <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-semibold">
          {step.step}
        </div>
      </div>
      <div className="flex-1">
        <h4 className="text-lg font-semibold text-gray-900 mb-1">
          {step.title}
        </h4>
        <p className="text-gray-600">
          {step.description}
        </p>
      </div>
      {index < (ingestionData?.workflow.steps.length || 0) - 1 && (
        <div className="flex-shrink-0 ml-4">
          <div className="w-0.5 h-12 bg-gray-200"></div>
        </div>
      )}
    </div>
  );

  // Render individual tool interface
  if (activeTool && ingestionData) {
    const toolData = Object.values(ingestionData.tools).find(tool => tool.id === activeTool);
    
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={handleBackToTools}
            className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <ArrowRight className="w-4 h-4 mr-2 transform rotate-180" />
            Back to Tools
          </button>
        </div>
        
        {activeTool === 'document-upload' && (
          <DocumentUpload 
            enterpriseId={enterpriseId} 
            onBack={handleBackToTools}
          />
        )}
        
        {activeTool === 'website-scraping' && (
          <WebsiteScraping 
            enterpriseId={enterpriseId} 
            onBack={handleBackToTools}
          />
        )}
        
        {activeTool === 'bulk-import' && (
          <BulkImportUpload 
            enterpriseId={enterpriseId} 
            onBack={handleBackToTools}
          />
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading ingestion tools...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">Error: {error}</span>
        </div>
      </div>
    );
  }

  if (!ingestionData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No ingestion data available</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {ingestionData.title}
            </h1>
            <p className="text-gray-600 text-lg">
              {ingestionData.description}
            </p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
          <div className="text-3xl font-bold mb-1">
            {ingestionData.stats.totalExtractions}
          </div>
          <div className="text-blue-100 text-sm">Total Extractions</div>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-lg shadow-lg">
          <div className="text-3xl font-bold mb-1">
            {ingestionData.stats.pendingReview}
          </div>
          <div className="text-orange-100 text-sm">Pending Review</div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <div className="text-3xl font-bold mb-1">
            {ingestionData.stats.successRate}%
          </div>
          <div className="text-green-100 text-sm">Success Rate</div>
        </div>
      </div>

      {/* Tools Grid */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Choose Your Import Method
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.values(ingestionData.tools).map(renderToolCard)}
        </div>
      </div>

      {/* Workflow */}
      <div className="bg-white rounded-lg border shadow-sm p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          How It Works
        </h2>
        <div className="space-y-6">
          {ingestionData.workflow.steps.map((step, index) => 
            renderWorkflowStep(step, index)
          )}
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            Ready to get started?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Select a tool above to begin importing your menu items. Our AI will help you extract and organize everything automatically.
          </p>
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-8 rounded-lg transition-colors shadow-lg hover:shadow-xl"
            onClick={() => {
              if (selectedTool) {
                handleStartTool(selectedTool);
              } else {
                setSelectedTool('document-upload');
              }
            }}
          >
            {selectedTool ? 'Start Selected Tool' : 'Start with Document Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenuIngestionTools;